import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AdvancedMarker, Polygon, Polyline, useMap } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { POLYGON_TYPES } from "./GoogleMap.interface";
const MIN_POLYGON_POINTS = 3;
const MIN_POLYLINE_POINTS = 2;
// Polygon colors by type
const COLOR_PROJECT_AREA_FILL = "#8CBAFF";
const COLOR_PROJECT_AREA_STROKE = "#0066FF";
const COLOR_JOB_SITE = "#272DBD";
const COLOR_CHARGE_STATION = "#18AEA5";
const POLYGON_FILL_OPACITY = 0.2;
const POLYGON_STROKE_WEIGHT_DEFAULT = 2;
const PROJECT_AREA_FILL_OPACITY = 0.1;
const PROJECT_AREA_STROKE_WEIGHT = 2.44;
const getPolygonOptions = (polygonType) => {
    const strokeWeight = POLYGON_STROKE_WEIGHT_DEFAULT;
    const fillOpacity = POLYGON_FILL_OPACITY;
    switch (polygonType) {
        case POLYGON_TYPES.PROJECT_AREA:
            return {
                fillColor: COLOR_PROJECT_AREA_FILL,
                fillOpacity: PROJECT_AREA_FILL_OPACITY,
                strokeColor: COLOR_PROJECT_AREA_STROKE,
                strokeWeight: PROJECT_AREA_STROKE_WEIGHT
            };
        case POLYGON_TYPES.JOB_SITE:
            return {
                fillColor: COLOR_JOB_SITE,
                fillOpacity,
                strokeColor: COLOR_JOB_SITE,
                strokeWeight
            };
        case POLYGON_TYPES.CHARGE_STATION:
            return {
                fillColor: COLOR_CHARGE_STATION,
                fillOpacity,
                strokeColor: COLOR_CHARGE_STATION,
                strokeWeight
            };
        default:
            return {
                fillColor: COLOR_PROJECT_AREA_FILL,
                fillOpacity: PROJECT_AREA_FILL_OPACITY,
                strokeColor: COLOR_PROJECT_AREA_STROKE,
                strokeWeight: PROJECT_AREA_STROKE_WEIGHT
            };
    }
};
const PolygonDrawingManager = ({ isDrawing, onClearPath, onSavePolygon, polygonType = POLYGON_TYPES.PROJECT_AREA }) => {
    const map = useMap();
    const [currentPath, setCurrentPath] = useState([]);
    const [cursorPosition, setCursorPosition] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    // Get polygon options based on type
    const polygonOptions = useMemo(() => getPolygonOptions(polygonType), [polygonType]);
    // Dashed stroke icons - only used for PROJECT_AREA
    const dashedStrokeIcons = useMemo(() => [
        {
            icon: {
                path: "M 0,-1 0,1",
                scale: PROJECT_AREA_STROKE_WEIGHT,
                strokeColor: polygonOptions.strokeColor,
                strokeOpacity: 1
            },
            offset: "0",
            repeat: "11px"
        }
    ], [polygonOptions.strokeColor]);
    useEffect(() => {
        if (!map || !isDrawing || isComplete) {
            return;
        }
        const clickListener = map.addListener("click", (event) => {
            const latLng = event.latLng;
            if (!latLng) {
                return;
            }
            setCurrentPath((previousPath) => [...previousPath, { lat: latLng.lat(), lng: latLng.lng() }]);
        });
        const moveListener = map.addListener("mousemove", (event) => {
            const latLng = event.latLng;
            if (!latLng) {
                return;
            }
            setCursorPosition({ lat: latLng.lat(), lng: latLng.lng() });
        });
        return () => {
            clickListener.remove();
            moveListener.remove();
        };
    }, [map, isDrawing, isComplete]);
    // leaving drawing mode discards the in-progress shape so re-entering always starts clean
    useEffect(() => {
        if (isDrawing) {
            return;
        }
        setCursorPosition(null);
        setIsComplete(false);
        if (currentPath.length > 0) {
            setCurrentPath([]);
            onClearPath?.();
        }
    }, [currentPath.length, isDrawing, onClearPath]);
    // crosshair cursor only while adding points (not when polygon is complete)
    useEffect(() => {
        if (!map) {
            return;
        }
        const showCrosshair = isDrawing && !isComplete;
        map.setOptions({ draggableCursor: showCrosshair ? "crosshair" : null });
        return () => map.setOptions({ draggableCursor: null });
    }, [isComplete, map, isDrawing]);
    const handlePathsChanged = useCallback((paths) => {
        const [outerRing] = paths;
        if (!outerRing) {
            return;
        }
        setCurrentPath(outerRing.map((point) => ({ lat: point.lat(), lng: point.lng() })));
    }, []);
    // clicking the first vertex closes the polygon once the minimum point count is reached
    const handleFirstVertexClick = useCallback((event) => {
        // keep the closing click from also reaching the map's drawing listener, which would append a duplicate point
        event.stopPropagation?.();
        if (currentPath.length >= MIN_POLYGON_POINTS) {
            setIsComplete(true);
        }
    }, [currentPath.length]);
    // the trailing line only guides the open path; the polygon renders its own edges once closed
    const previewLinePath = useMemo(() => {
        if (isComplete) {
            return [];
        }
        return cursorPosition ? [...currentPath, cursorPosition] : currentPath;
    }, [currentPath, cursorPosition, isComplete]);
    useEffect(() => {
        if (!isComplete || currentPath.length < MIN_POLYGON_POINTS) {
            return;
        }
        onSavePolygon({
            id: crypto.randomUUID(),
            paths: currentPath,
            polygonType
        });
        setCurrentPath([]);
        setCursorPosition(null);
        setIsComplete(false);
    }, [currentPath, isComplete, onSavePolygon, polygonType]);
    if (!isDrawing) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [!isComplete &&
                currentPath.map((point, index) => (_jsx(AdvancedMarker, { onClick: index === 0 ? handleFirstVertexClick : undefined, position: point, title: index === 0 ? "Close polygon" : undefined, children: _jsx("div", { className: `rounded-full border border-[#666666] shadow-sm ${index === 0 ? "h-5 w-5 cursor-pointer bg-[#FFCD11]" : "h-3.5 w-3.5 bg-white"}`, "data-test": "vertex-dot" }) }, `vertex-marker-${index}`))), previewLinePath.length >= MIN_POLYLINE_POINTS &&
                (polygonType === POLYGON_TYPES.PROJECT_AREA ? (_jsx(Polyline, { icons: dashedStrokeIcons, path: previewLinePath, strokeOpacity: 0 })) : (_jsx(Polyline, { path: previewLinePath, strokeColor: polygonOptions.strokeColor, strokeOpacity: 0.8, strokeWeight: polygonOptions.strokeWeight }))), isComplete && currentPath.length >= MIN_POLYGON_POINTS && (_jsxs(_Fragment, { children: [_jsx(Polygon, { editable: true, fillColor: polygonOptions.fillColor, fillOpacity: polygonOptions.fillOpacity, onPathsChanged: handlePathsChanged, paths: currentPath, strokeColor: polygonOptions.strokeColor, strokeOpacity: polygonType === POLYGON_TYPES.PROJECT_AREA ? 0 : 0.8, strokeWeight: polygonOptions.strokeWeight }), polygonType === POLYGON_TYPES.PROJECT_AREA && (_jsx(Polyline, { icons: dashedStrokeIcons, path: [...currentPath, currentPath[0]], strokeOpacity: 0 }))] }))] }));
};
export default PolygonDrawingManager;
